import { GetServerSidePropsContext } from 'next'

import { store } from 'redux/store'

import { DeckResponse } from 'services/apiTypes/deck.types'
import { CardType, generateDefaultCategory } from 'types/deck'

import ToastService from 'services/toast.service'
import CardService, { CardService as CardServiceServer } from 'services/card.service'
import { SearchOptions } from 'services/apiTypes/card.types'

import mapToDeck from './MapToDeck'

type QueryStringCategory = 'm' | 's' | 'c' | 'a'
type QueryString = {
  u: string
  q: number
  f: number
  c: QueryStringCategory
}

const allowedCategories: QueryStringCategory[] = ['m', 's', 'c', 'a']

const convertQueryCategegoryToCategory = (categoryName: QueryStringCategory, card?: CardType) => {
  if (categoryName === 'a') return 'Attraction'
  if (categoryName === 's') return 'Sideboard'
  if (categoryName === 'c') return 'Commander'

  return card?.categories[0] || 'Mainboard'
}

export function generateDeckAsQueryParam(): string {
  const state = store.getState()

  const { cardMap, categories } = state.deck

  const cards: QueryString[] = Object.values(cardMap).reduce((acc, card) => {
    const categoryName = card.categories[0]
    const category = categories[categoryName] || generateDefaultCategory()

    if (!category.includedInDeck || !categoryName) return acc

    let categoryType: QueryStringCategory = 'm'

    if (categoryName === 'Sideboard' && !category.isPremier && category.includedInDeck) categoryType = 's'
    else if (category.isPremier && category.includedInDeck) categoryType = 'c'
    else if (categoryName.toLocaleLowerCase().includes('attraction')) categoryType = 'a'

    acc.push({
      u: card.uid,
      q: card.qty,
      f: card.modifier === 'Normal' ? 0 : 1,
      c: categoryType,
    })

    return acc
  }, new Array<QueryString>())

  return encodeURIComponent(JSON.stringify(cards))
}

export async function getDeckFromUrl(
  queryString: string = '',
  serverContext?: GetServerSidePropsContext,
): Promise<DeckResponse> {
  const queryStringCards: QueryString[] = []

  try {
    // NOT TYPE SAFE, LEAVING IT THIS WAY FOR CODE MAINTAINABILITY, BUT WE NEED TO SAFTEY CHECK THIS BELOW STILL
    const parsedQueryString: QueryString[] = JSON.parse(decodeURIComponent(queryString))

    for (const queryStringShape of parsedQueryString) {
      if (typeof queryStringShape.u !== 'string') throw new Error('UIDs are expected to be strings')
      if (typeof queryStringShape.q !== 'number') throw new Error('Quantities are expected to be numbers')
      if (typeof queryStringShape.f !== 'number') throw new Error('Foil flags are expected as numbers')
      if (!allowedCategories.includes(queryStringShape.c))
        throw new Error(`Expected categories are ${allowedCategories}`)

      queryStringCards.push({ ...queryStringShape })
    }
  } catch (e) {
    ToastService.create('Unable to parse cards from URL, no cards found')
  }

  let next = ''
  let totalLookups = 0
  let searchOptions: SearchOptions = { uids: queryStringCards.map(card => card.u) }

  const cardMap: Record<string, CardType> = {}

  while (searchOptions.uids?.length || next) {
    // Failsafe to prevent loading arbitrary decks larger than 4 pages.
    // This will allow for 40 * 4 cards to be loaded for an arbitrary deck
    if (totalLookups > 3) break

    const response = !!serverContext
      ? await new CardServiceServer(serverContext).list(searchOptions, next)
      : await CardService.list(searchOptions, next)

    totalLookups += 1

    const responseMap = response.cards.reduce(
      (acc, card) => {
        acc[card.uid] = card
        return acc
      },
      {} as Record<string, CardType>,
    )

    queryStringCards.forEach(card => {
      const cardData = responseMap[card.u]

      if (!cardData) return

      cardMap[cardData.id] = {
        ...cardData,
        qty: card.q || 1,
        modifier: card.f > 0 ? 'Foil' : 'Normal',
        categories: [convertQueryCategegoryToCategory(card.c, responseMap[card.u] || undefined)],
      }
    })

    if (response.next) {
      next = response.next
      searchOptions = {}
    } else {
      next = ''
      searchOptions = {}
    }
  }

  const convertedCards = mapToDeck(cardMap)

  const psudoDeckResponse: DeckResponse = {
    cards: convertedCards,
    customCards: [], // TODO - rework converted cards to break out custom cards & cards
    categories: [
      {
        id: 0,
        name: 'Mainboard',
        includedInDeck: true,
        includedInPrice: true,
        isPremier: false,
      },
      {
        id: 0,
        name: 'Sideboard',
        includedInDeck: true,
        includedInPrice: false,
        isPremier: false,
      },
      {
        id: 0,
        name: 'Maybeboard',
        includedInDeck: false,
        includedInPrice: false,
        isPremier: false,
      },
      {
        id: 0,
        name: 'Commander',
        includedInDeck: true,
        includedInPrice: true,
        isPremier: true,
      },
      {
        id: 0,
        name: 'Attraction',
        includedInDeck: false,
        includedInPrice: true,
        isPremier: false,
      },
      {
        id: 0,
        name: 'Tokens & Extras',
        includedInDeck: false,
        includedInPrice: true,
        isPremier: false,
      },
    ],
    id: 0,
    owner: {
      id: 0,
      username: '',
      avatar: '',
      frame: '',
      ckAffiliate: '',
      tcgAffiliate: '',
      referrerEnum: null,
    },
    commentRoot: 0,
    points: 0,
    userInput: 0,
    name: '',
    description: '',
    viewCount: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    featured: '',
    customFeatured: '',
    playmat: '',
    size: 0,
    private: false,
    unlisted: false,
    theorycrafted: true,
    deckFormat: 7, // custom
    edhBracket: null,
    folder: 0,
    parentFolder: 0,
    game: 0,
    upvotes: [],
    downvotes: [],
    colors: [],
    viewers: [],
    editors: [],
    bookmarked: false,
    deckTags: [],
    cardPackage: null,
    playgroupDeckUrl: null,
  }

  return Promise.resolve(psudoDeckResponse)
}
